import { Controller } from "@hotwired/stimulus"

interface CloudinaryUploadInfo {
  public_id: string
  secure_url: string
  resource_type: "image" | "video" | "raw"
  format: string
  original_filename: string
  bytes: number
  pages?: number
}

interface CloudinaryWidgetResult {
  event: string
  info: CloudinaryUploadInfo
}

interface CloudinaryWidget {
  open(): void
  destroy(options?: { removeThumbnails?: boolean }): Promise<void>
}

declare global {
  interface Window {
    Stimulus: import("@hotwired/stimulus").Application
    Turbo: { renderStreamMessage(html: string): void }
  }

  const cloudinary: {
    createUploadWidget(
      options: Record<string, unknown>,
      callback: (error: unknown, result: CloudinaryWidgetResult) => void
    ): CloudinaryWidget
  }
}

// Stream-render rate-limit: when 5 files come in over the Cloudinary widget,
// each `create` returns a turbo_stream replacing the entire panel. Without a
// debounce the panel flickers and concurrent renders can race. We coalesce
// renders into the next animation frame.
const TURBO_STREAM_ACCEPT = "text/vnd.turbo-stream.html"

export default class extends Controller {
  static targets = ["fileList", "messages", "continueButton", "mediaType", "details", "inference", "inferenceLoader", "dealership", "submissionTypeContainer", "submissionTypeGrid", "structuredDataPanel"]
  static values = {
    cloudName: String,
    apiKey: String,
    folder: String,
    signatureUrl: String,
    submissionId: Number,
    mediaFilesUrl: String,
    submissionUrl: String,
    submissionTypeUrl: String
  }

  declare readonly fileListTarget: HTMLElement
  declare readonly hasFileListTarget: boolean
  declare readonly messagesTarget: HTMLElement
  declare readonly hasMessagesTarget: boolean
  declare readonly continueButtonTarget: HTMLElement
  declare readonly hasContinueButtonTarget: boolean
  declare readonly mediaTypeTarget: HTMLInputElement
  declare readonly hasMediaTypeTarget: boolean
  declare readonly detailsTarget: HTMLTextAreaElement
  declare readonly inferenceLoaderTarget: HTMLElement
  declare readonly hasInferenceTarget: boolean
  declare readonly hasInferenceLoaderTarget: boolean
  declare readonly dealershipTarget: HTMLSelectElement
  declare readonly hasDealershipTarget: boolean
  declare readonly submissionTypeContainerTarget: HTMLElement
  declare readonly hasSubmissionTypeContainerTarget: boolean
  declare readonly submissionTypeGridTarget: HTMLElement
  declare readonly hasSubmissionTypeGridTarget: boolean
  declare readonly structuredDataPanelTarget: HTMLElement
  declare readonly hasStructuredDataPanelTarget: boolean
  declare cloudNameValue: string
  declare apiKeyValue: string
  declare folderValue: string
  declare signatureUrlValue: string
  declare submissionIdValue: number
  declare mediaFilesUrlValue: string
  declare submissionUrlValue: string
  declare submissionTypeUrlValue: string

  private widget: CloudinaryWidget | null = null
  private inferenceObserver: MutationObserver | null = null

  connect(): void {
    this.observeInference()
  }

  disconnect(): void {
    if (this.widget) {
      this.widget.destroy()
      this.widget = null
    }
    if (this.inferenceObserver) {
      this.inferenceObserver.disconnect()
      this.inferenceObserver = null
    }
  }

  open(): void {
    if (!this.widget) {
      this.widget = cloudinary.createUploadWidget(
        {
          cloudName: this.cloudNameValue,
          apiKey: this.apiKeyValue,
          uploadSignature: this.generateSignature.bind(this),
          uploadPrefix: "https://api-eu.cloudinary.com/",
          folder: this.folderValue,
          resourceType: "auto",
          sources: ["local"],
          multiple: true,
          maxFiles: 20,
          clientAllowedFormats: ["pdf", "jpg", "jpeg", "png", "mp4", "mov", "zip", "xlsx", "xls", "csv"],
          maxFileSize: 104857600,
          showPoweredBy: false
        },
        this.handleWidget.bind(this)
      )
    }
    this.widget.open()
  }

  private generateSignature(callback: (signature: string) => void, paramsToSign: Record<string, string>): void {
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')!.content
    fetch(this.signatureUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify({ params_to_sign: paramsToSign })
    })
      .then(response => response.text())
      .then(signature => callback(signature))
  }

  private handleWidget(error: unknown, result: CloudinaryWidgetResult): void {
    if (error) {
      console.error("Upload error:", error)
      this.showMessage("error", "Upload failed. Please try again.")
      return
    }

    if (result.event === "success") {
      this.saveMediaFile(result.info)
    }
  }

  private async saveMediaFile(info: CloudinaryUploadInfo): Promise<void> {
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')!.content
    const response = await fetch(this.mediaFilesUrlValue, {
      method: "POST",
      headers: {
        "Accept": TURBO_STREAM_ACCEPT,
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify({
        media_file: {
          cloudinary_public_id: info.public_id,
          secure_url: info.secure_url,
          resource_type: info.resource_type,
          format: info.format,
          original_filename: info.original_filename,
          bytes: info.bytes,
          pages: info.pages || null
        }
      })
    })

    if (response.ok) {
      window.Turbo.renderStreamMessage(await response.text())
      this.afterFileChange()
      this.revealStructuredDataPanel()
      this.showInferenceLoader()
      this.showMessage("success", `${info.original_filename} uploaded successfully`)
    } else {
      const data = await response.json().catch(() => ({ errors: ["Upload failed"] }))
      const errs = Array.isArray(data.errors) ? data.errors.join(", ") : "Upload failed"
      this.showMessage("error", `${info.original_filename}: ${errs}`)
    }
  }

  async removeFile(event: Event): Promise<void> {
    event.preventDefault()
    event.stopPropagation()
    const target = event.currentTarget as HTMLElement
    const tile = target.closest<HTMLElement>("[data-media-file-id]")
    if (!tile) return

    const id = tile.dataset.mediaFileId
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')!.content

    const response = await fetch(`${this.mediaFilesUrlValue.replace(/\/+$/, "")}/${id}`, {
      method: "DELETE",
      headers: {
        "Accept": TURBO_STREAM_ACCEPT,
        "X-CSRF-Token": csrfToken
      }
    })

    if (response.ok) {
      window.Turbo.renderStreamMessage(await response.text())
      this.afterFileChange()
    }
  }

  // Called after the panel is replaced via turbo_stream so the Continue button
  // re-evaluates against the new file count. Stimulus rebinds `fileListTarget`
  // automatically (MutationObserver), so by the time this runs the target is
  // the freshly-rendered grid.
  private afterFileChange(): void {
    this.updateContinueState()
  }

  async saveSubmissionDetails(): Promise<void> {
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')!.content
    const mediaType = this.mediaTypeTarget.value
    const details = this.detailsTarget.value
    // NOTE: submission_type is intentionally NOT sent here — it has its
    // own dedicated handler (saveSubmissionType) so manual changes always
    // set source: "manual" and AI inference can never clobber a user choice.
    const submission: Record<string, string> = { media_type: mediaType, details }
    if (this.hasDealershipTarget) {
      const selectedOption = this.dealershipTarget.selectedOptions[0]
      if (selectedOption) {
        // The Submission#mirror_dealer_to_legacy_columns server-side callback
        // populates dealership_code/name from the chosen dealer, so we only
        // send dealer_id here.
        submission.dealer_id = selectedOption.value
      }
    }

    await fetch(this.submissionUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify({ submission })
    })

    this.updateContinueState()
  }

  // Wired to the new 2-card segmented control. Reads the chosen value from
  // the clicked card's data attribute, hits the dedicated submission_type
  // endpoint (locks source: "manual"), and updates the visible selected
  // state on the cards.
  async selectSubmissionType(event: Event): Promise<void> {
    const button = event.currentTarget as HTMLButtonElement
    const submissionType = button.dataset.submissionType
    if (!submissionType) return

    if (this.hasSubmissionTypeGridTarget) {
      const cards = this.submissionTypeGridTarget.querySelectorAll<HTMLButtonElement>("[data-submission-type]")
      cards.forEach(card => {
        const isSelected = card === button
        card.classList.toggle("border-accent", isSelected)
        card.classList.toggle("bg-accent/10", isSelected)
        card.classList.toggle("border-base-300", !isSelected)
        card.classList.toggle("bg-base-100", !isSelected)
        card.classList.toggle("hover:border-accent/50", !isSelected)

        const icon = card.querySelector<SVGElement>("svg")
        if (icon) {
          icon.classList.toggle("text-accent", isSelected)
          icon.classList.toggle("text-base-content/60", !isSelected)
        }
      })
    }

    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')!.content
    await fetch(this.submissionTypeUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify({ submission: { submission_type: submissionType } })
    })

    // Clear the AI-detected badge optimistically — server flips source to "manual"
    // and any subsequent Turbo broadcast from FileRoleClassificationJob will be
    // suppressed (server-side `submission_type_locked?` check).
    if (this.hasSubmissionTypeContainerTarget) {
      const badge = this.submissionTypeContainerTarget.querySelector(".badge.badge-info")
      badge?.remove()
    }
  }

  private fileTilesCount(): number {
    if (!this.hasFileListTarget) return 0
    // Each MediaFile becomes one or more `[data-media-file-id]` elements
    // (PDFs render one tile per page). Count distinct file ids.
    const ids = new Set<string>()
    this.fileListTarget.querySelectorAll<HTMLElement>("[data-media-file-id]").forEach(el => {
      const id = el.dataset.mediaFileId
      if (id) ids.add(id)
    })
    return ids.size
  }

  private updateContinueState(): void {
    if (!this.hasContinueButtonTarget || !this.hasMediaTypeTarget) return

    const hasFiles = this.fileTilesCount() > 0
    const hasMediaType = !!this.mediaTypeTarget.value
    const canContinue = hasFiles && hasMediaType

    this.continueButtonTarget.classList.toggle("btn-disabled", !canContinue)
    if (!canContinue) {
      this.continueButtonTarget.setAttribute("aria-disabled", "true")
      this.continueButtonTarget.setAttribute("tabindex", "-1")
    } else {
      this.continueButtonTarget.removeAttribute("aria-disabled")
      this.continueButtonTarget.removeAttribute("tabindex")
    }
  }

  private showMessage(type: "success" | "error", text: string): void {
    if (!this.hasMessagesTarget) return

    const alert = document.createElement("div")
    alert.className = type === "error"
      ? "alert alert-error text-sm"
      : "alert alert-success text-sm"
    alert.textContent = text

    this.messagesTarget.appendChild(alert)

    if (type === "success") {
      setTimeout(() => alert.remove(), 4000)
    }
  }

  private observeInference(): void {
    const inferenceEl = document.getElementById("media-type-inference")
    if (!inferenceEl) return

    const observeTarget = inferenceEl.parentElement || document.body

    this.inferenceObserver = new MutationObserver(() => {
      const el = document.getElementById("media-type-inference")
      if (!el) return

      const inferred = el.dataset.inferredMediaType
      const status = el.dataset.classificationStatus

      // Any definitive outcome (success, no-media, or failure) ends the
      // spinner. Hide unconditionally so a race where the dropdown already
      // has a value (manual pick, prior inference) can't leave it stuck.
      const settled = inferred || status === "no-media" || status === "failed"
      if (settled) this.hideInferenceLoader()

      // Only auto-fill the dropdown when it's still empty — never overwrite a
      // user's manual choice.
      if (inferred && !this.mediaTypeTarget.value) {
        this.mediaTypeTarget.value = inferred
        this.updatePickerVisual(inferred)
        this.saveSubmissionDetails()
      } else if (status === "failed") {
        this.showMessage("error", `Media type detection failed: ${el.dataset.errorMessage || "unknown error"}. Please pick a type manually.`)
      }
    })

    this.inferenceObserver.observe(observeTarget, {
      childList: true,
      subtree: true
    })
  }

  // Sync the media-type-picker tile + selected state when AI inference fills
  // in the value. The hidden input itself is updated by the caller.
  private updatePickerVisual(mediaType: string): void {
    const picker = this.element.querySelector('[data-controller~="media-type-picker"]')
    if (!picker) return

    const label = picker.querySelector<HTMLElement>('[data-media-type-picker-target="triggerLabel"]')
    if (label) label.textContent = mediaType

    picker.querySelectorAll<HTMLButtonElement>("[data-media-type]").forEach(btn => {
      const isSelected = btn.dataset.mediaType === mediaType
      btn.classList.toggle("border-accent", isSelected)
      btn.classList.toggle("bg-accent/10", isSelected)
      btn.classList.toggle("text-accent", isSelected)
      btn.classList.toggle("border-transparent", !isSelected)
      btn.classList.toggle("hover:bg-base-200", !isSelected)
    })
  }

  private revealStructuredDataPanel(): void {
    if (this.hasStructuredDataPanelTarget) {
      this.structuredDataPanelTarget.classList.remove("hidden")
    }
  }

  private showInferenceLoader(): void {
    const hasFiles = this.fileTilesCount() > 0
    if (hasFiles && this.hasMediaTypeTarget && !this.mediaTypeTarget.value && this.hasInferenceLoaderTarget) {
      this.inferenceLoaderTarget.style.display = "flex"
    }
  }

  private hideInferenceLoader(): void {
    if (this.hasInferenceLoaderTarget) {
      this.inferenceLoaderTarget.style.display = "none"
    }
  }
}
